import { useNavigate } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Download, FileStack, LayoutGrid, List, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { Commitment } from "@/hooks/use-commitments";
import { useCommitments } from "@/hooks/use-commitments";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

type SortField =
  | "commitmentNumber"
  | "commitmentType"
  | "projectName"
  | "companyName"
  | "title"
  | "status"
  | "originalAmount"
  | "revisedAmount";

type SortDir = "asc" | "desc";
type ViewMode = "table" | "tile";

const VIEW_MODE_STORAGE_KEY = "commitments:viewMode";

function readViewMode(): ViewMode {
  if (typeof window === "undefined") return "table";
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === "tile" ? "tile" : "table";
}

interface ColumnDef {
  field: SortField;
  label: string;
  alignRight?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { field: "commitmentNumber", label: "Number" },
  { field: "commitmentType", label: "Type" },
  { field: "projectName", label: "Project" },
  { field: "companyName", label: "Company" },
  { field: "title", label: "Title" },
  { field: "status", label: "Status" },
  { field: "originalAmount", label: "Original Amount", alignRight: true },
  { field: "revisedAmount", label: "Revised Amount", alignRight: true },
];

function formatCurrency(value: string | null): string {
  if (!value) return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function formatCommitmentType(type: string | null): string {
  if (!type) return "--";
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "active":
    case "approved":
    case "executed":
      return "success";
    case "draft":
    case "pending":
    case "pending_execution":
      return "warning";
    case "completed":
    case "closed":
      return "secondary";
    case "cancelled":
    case "void":
    case "terminated":
      return "destructive";
    default:
      return "default";
  }
}

function compareCommitments(a: Commitment, b: Commitment, field: SortField, dir: SortDir): number {
  let cmp = 0;
  switch (field) {
    case "commitmentNumber":
      cmp = (a.commitmentNumber ?? "").localeCompare(b.commitmentNumber ?? "");
      break;
    case "commitmentType":
      cmp = (a.commitmentType ?? "").localeCompare(b.commitmentType ?? "");
      break;
    case "projectName":
      cmp = (a.projectName ?? "").localeCompare(b.projectName ?? "");
      break;
    case "companyName":
      cmp = (a.companyName ?? "").localeCompare(b.companyName ?? "");
      break;
    case "title":
      cmp = (a.title ?? "").localeCompare(b.title ?? "");
      break;
    case "status":
      cmp = (a.status ?? "").localeCompare(b.status ?? "");
      break;
    case "originalAmount":
      cmp = Number(a.originalAmount ?? 0) - Number(b.originalAmount ?? 0);
      break;
    case "revisedAmount":
      cmp = Number(a.revisedAmount ?? 0) - Number(b.revisedAmount ?? 0);
      break;
  }
  return dir === "asc" ? cmp : -cmp;
}

export function MyCommitmentsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useCommitments();

  const [viewMode, setViewMode] = useState<ViewMode>(() => readViewMode());
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("commitmentNumber");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const items = useMemo(() => data ?? [], [data]);

  const displayItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = items.filter((c) => {
      if (q === "") return true;
      const haystack = [
        c.commitmentNumber,
        c.commitmentType,
        c.projectName,
        c.companyName,
        c.title,
        c.status,
      ]
        .filter((v): v is string => typeof v === "string" && v.length > 0)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    return [...filtered].sort((a, b) => compareCommitments(a, b, sortField, sortDir));
  }, [items, search, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function handleExport() {
    const rows = displayItems.map((c) => ({
      commitmentNumber: c.commitmentNumber ?? "",
      commitmentType: formatCommitmentType(c.commitmentType),
      projectName: c.projectName ?? "",
      companyName: c.companyName ?? "",
      title: c.title ?? "",
      status: c.status ?? "",
      originalAmount: c.originalAmount ?? "",
      revisedAmount: c.revisedAmount ?? "",
    }));
    downloadCsv("commitments", rows, [
      { key: "commitmentNumber", label: "Number" },
      { key: "commitmentType", label: "Type" },
      { key: "projectName", label: "Project" },
      { key: "companyName", label: "Company" },
      { key: "title", label: "Title" },
      { key: "status", label: "Status" },
      { key: "originalAmount", label: "Original Amount" },
      { key: "revisedAmount", label: "Revised Amount" },
    ]);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load commitments"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Commitments</h1>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search commitments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={displayItems.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <div className="inline-flex items-center rounded-md border bg-background p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-sm transition-colors",
                viewMode === "table"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="Table view"
              aria-pressed={viewMode === "table"}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("tile")}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-sm transition-colors",
                viewMode === "tile"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="Tile view"
              aria-pressed={viewMode === "tile"}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Showing <span className="font-semibold text-foreground">{displayItems.length}</span> of{" "}
        {items.length} commitments
      </p>

      {displayItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileStack className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>
              {items.length === 0 ? "No commitments found." : "No commitments match your search."}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === "table" ? (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                {COLUMNS.map((col) => {
                  const active = sortField === col.field;
                  return (
                    <th
                      key={col.field}
                      className={cn(
                        "px-4 py-3 text-xs font-medium text-muted-foreground",
                        col.alignRight ? "text-right" : "text-left",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col.field)}
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground",
                          col.alignRight && "ml-auto",
                        )}
                      >
                        <span>{col.label}</span>
                        {active ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : null}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayItems.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer border-b transition-colors last:border-b-0 hover:bg-muted/30"
                  onClick={() => navigate({ to: "/commitments/$id", params: { id: c.id } })}
                >
                  <td className="px-4 py-3 font-medium">{c.commitmentNumber ?? "--"}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatCommitmentType(c.commitmentType)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.projectName ?? "--"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.companyName ?? "--"}</td>
                  <td className="px-4 py-3">{c.title ?? "Untitled"}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(c.status)}>
                      {c.status?.replace(/_/g, " ") ?? "--"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(c.originalAmount)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(c.revisedAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayItems.map((c) => (
            <Card
              key={c.id}
              className="flex h-full cursor-pointer flex-col transition-colors hover:border-primary/30"
              onClick={() => navigate({ to: "/commitments/$id", params: { id: c.id } })}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base font-semibold leading-tight">
                    {c.commitmentNumber ?? "No Number"}
                  </CardTitle>
                  <Badge variant={statusVariant(c.status)}>
                    {c.status?.replace(/_/g, " ") ?? "--"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col space-y-3 text-sm">
                <p className="truncate font-medium">{c.title ?? "Untitled commitment"}</p>
                <div className="space-y-1 text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Type</span>
                    <span className="font-medium text-foreground">
                      {formatCommitmentType(c.commitmentType)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="shrink-0">Project</span>
                    <span className="truncate text-right font-medium text-foreground">
                      {c.projectName ?? "--"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="shrink-0">Company</span>
                    <span className="truncate text-right font-medium text-foreground">
                      {c.companyName ?? "--"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Original Amount</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(c.originalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Revised Amount</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(c.revisedAmount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
